import { Landmark, TrendingDown, TrendingUp, AlertTriangle } from "lucide-react";
import { fmtCurrency } from "@/lib/api";

const pct = (v) => `${v > 0 ? "+" : ""}${(v * 100).toFixed(1)}%`;
const prob = (v) => `${(v * 100).toFixed(1)}%`;
const signed = (v) => (v > 0 ? "+" : "") + fmtCurrency(v);
const th = "px-3 py-2 font-display uppercase text-[10px] tracking-widest";
const loanTerms = (p) => `${(p.loan_rate * 100).toFixed(1)} % / ${p.loan_term} j`;

// Resultaatblok "Budgetafwijking en financiering" (Monte Carlo): meerkosten uit eigen kas, lenen zodra de kas
// in het uitvoeringsjaar onder de ondergrens zakt. Per project en totaal afwijking, meerkosten, extra lening, kansen.
export default function BudgetDeviationBlock({ t, data }) {
  const { projects, total, case_p10: lo, case_p90: hi, base, cash_floor: floor, runs } = data;
  const Case = ({ label, c, tone, testid }) => (
    <div className={`border-2 p-3 ${tone === "down" ? "border-positive bg-positive/10" : "border-destructive bg-destructive/10"}`} data-testid={testid}>
      <div className="stat-label flex items-center gap-1">{tone === "down" ? <TrendingDown className="w-3.5 h-3.5 text-positive" /> : <TrendingUp className="w-3.5 h-3.5 text-destructive" />}{label}</div>
      <div className="font-display font-black text-xl mt-1 font-mono-num">{signed(c.cost_mean)} <span className="text-xs font-normal text-muted-foreground">{t("meerkosten gem.")}</span></div>
      <div className="text-[11px] mt-1 space-y-0.5">
        <div>{t("Extra lenen gem.")}: <b className="font-mono-num">{fmtCurrency(c.extra_loan_mean)}</b> <span className="text-muted-foreground">· {t("financiering nodig in")} {prob(c.prob_financing)}</span></div>
        <div>{t("NPV gem.")}: <b className="font-mono-num">{fmtCurrency(c.npv_mean)}</b> <span className="text-muted-foreground">({signed(c.npv_mean - base.npv_mean)} {t("t.o.v. alle runs")})</span></div>
        <div>{t("Laagste kas gem.")}: <b className="font-mono-num">{fmtCurrency(c.min_cash_mean)}</b> <span className="text-muted-foreground">({signed(c.min_cash_mean - base.min_cash_mean)})</span></div>
      </div>
    </div>
  );

  return (
    <div className="border-2 border-foreground p-3 bg-card space-y-3" data-testid="mc-budget-deviation">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="font-display uppercase font-bold text-xs tracking-widest inline-flex items-center gap-1.5"><Landmark className="w-4 h-4" /> {t("Budgetafwijking en financiering")}</p>
        <p className="text-[10px] text-muted-foreground italic max-w-xl">{t("Meerkosten komen uit eigen kas; zakt de kas in het uitvoeringsjaar onder de ondergrens van")} {fmtCurrency(floor)}, {t("dan wordt het tekort (max. de meerkosten) geleend tegen de leningvoorwaarden van het project.")}</p>
      </div>

      <div className="border-2 border-accent bg-accent/10 p-3 flex items-start gap-2" data-testid="mc-bd-financing-note">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <p className="text-sm">
          {t("In")} <b className="font-mono-num">{prob(total.prob_financing)}</b> {t("van de runs")} ({total.runs_financing} {t("van")} {runs}) {t("was extra financiering nodig omdat de kas in het uitvoeringsjaar (mede) door hogere projectkosten onder")} <b className="font-mono-num">{fmtCurrency(floor)}</b> {t("lag")}.
          {total.runs_financing > 0 && <> {t("Extra lening als dat nodig was: gemiddeld")} <b className="font-mono-num">{fmtCurrency(total.extra_loan_mean_when_needed)}</b>, {t("maximaal")} <b className="font-mono-num">{fmtCurrency(total.extra_loan_max)}</b>.</>}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[[t("Meerkosten") + " · P10", total.cost_p10, "mc-bd-total-cost-p10"], [t("Meerkosten") + " · P50", total.cost_p50, "mc-bd-total-cost-p50"], [t("Meerkosten") + " · P90", total.cost_p90, "mc-bd-total-cost-p90"]].map(([l, v, id]) => (
          <div key={id} className="border-2 border-foreground p-3" data-testid={id}>
            <div className="stat-label">{l}</div>
            <div className={`font-display font-black text-xl mt-1 font-mono-num ${v > 0 ? "text-destructive" : v < 0 ? "text-positive" : ""}`}>{signed(v)}</div>
          </div>
        ))}
        <div className="border-2 border-foreground p-3" data-testid="mc-bd-total-loan">
          <div className="stat-label">{t("Extra lenen")} · P50 / P90</div>
          <div className="font-display font-black text-xl mt-1 font-mono-num">{fmtCurrency(total.extra_loan_p50)} <span className="text-sm text-muted-foreground">/ {fmtCurrency(total.extra_loan_p90)}</span></div>
          <div className="text-[10px] text-muted-foreground">{t("gem.")} {fmtCurrency(total.extra_loan_mean)} {t("over alle runs")}</div>
        </div>
      </div>

      <div className="overflow-x-auto border-2 border-border">
        <table className="w-full text-sm" data-testid="mc-bd-table">
          <thead className="bg-foreground text-background">
            <tr>
              <th className={`${th} text-left`}>{t("Project")}</th>
              <th className={`${th} text-right`}>{t("Budget")}</th>
              <th className={`${th} text-right`}>{t("Afwijking P10 / P50 / P90")}</th>
              <th className={`${th} text-right`}>{t("Meerkosten P50 / P90")}</th>
              <th className={`${th} text-right`}>{t("Extra lenen P50 / P90")}</th>
              <th className={`${th} text-right`}>{t("Kans financiering")}</th>
              <th className={`${th} text-right`}>{t("Kans > 10 % / > 20 %")}</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p, i) => (
              <tr key={p.key} className="border-t border-border" data-testid={`mc-bd-row-${i}`}>
                <td className="px-3 py-2 font-medium">{p.name}
                  <div className="text-[10px] text-muted-foreground">{p.has_loan ? `${t("lening")} ${fmtCurrency(p.loan)} · ${loanTerms(p)}` : `${t("geen lening")} · ${t("bij tekort")} ${loanTerms(p)}`}</div>
                  {p.base_below_floor && (
                    <div className="text-[10px] text-destructive inline-flex items-center gap-1" data-testid={`mc-bd-base-low-${i}`}>
                      <AlertTriangle className="w-3 h-3" /> {t("kas")} {p.start_year} {t("in basisscenario")} {fmtCurrency(p.base_cash_start_year)} — {t("al onder de ondergrens: elke overschrijding wordt geleend")}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono-num">{fmtCurrency(p.amount_nominal)}
                  {p.money_year !== p.start_year && <div className="text-[10px] text-muted-foreground">{t("prijspeil")} {p.start_year} · {t("ingevoerd")} {fmtCurrency(p.amount)} ({p.money_year})</div>}
                </td>
                <td className="px-3 py-2 text-right font-mono-num text-xs">{pct(p.dev_p10)} / {pct(p.dev_p50)} / {pct(p.dev_p90)}</td>
                <td className="px-3 py-2 text-right font-mono-num text-xs">{signed(p.cost_p50)} / <span className="text-destructive">{signed(p.cost_p90)}</span></td>
                <td className="px-3 py-2 text-right font-mono-num text-xs">{fmtCurrency(p.extra_loan_p50)} / <span className="text-destructive">{fmtCurrency(p.extra_loan_p90)}</span></td>
                <td className="px-3 py-2 text-right font-mono-num">{prob(p.prob_financing)}</td>
                <td className="px-3 py-2 text-right font-mono-num text-xs">{prob(p.prob_over_10)} / {prob(p.prob_over_20)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-muted-foreground space-y-0.5" data-testid="mc-bd-mean-note">
        {projects.map((p) => (
          <div key={p.key}>
            <b>{p.name}</b>: {t("verwachte afwijking gemiddeld")} <span className="font-mono-num">{pct(p.dev_mean)}</span>
            {p.cfg?.distribution === "split_normal" && <> ({t("modus")} {pct(p.cfg.base || 0)}, P10 {pct(p.cfg.low || 0)} / P90 {pct(p.cfg.high || 0)})</>}
          </div>
        ))}
        <div className="italic">{t("Bij een scheve verdeling ligt het gemiddelde boven de modus: overschrijden komt vaker voor dan onderschrijden.")}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Case label={t("Gunstige 10 % (laagste meerkosten)")} c={lo} tone="down" testid="mc-bd-case-p10" />
        <Case label={t("Ongunstige 10 % (hoogste meerkosten)")} c={hi} tone="up" testid="mc-bd-case-p90" />
      </div>
    </div>
  );
}
